export const LANGUAGE_FIELDS = {
  cultures: {
    fields: {
      fr: 'french_name',
      en: 'english_name',
      lat: 'latin_name',
    },
    labels: {
      fr: 'Français',
      en: 'Anglais',
      lat: 'Latin',
    },
    defaultLanguage: 'fr',
  },
};

// Formats a substance-name option as "Name (cas1, cas2)" using the CAS
// numbers the backend attaches via include_source_fields. When the substance
// has no CAS recorded, just the name is shown.
const formatSubstanceNameLabel = (option, source) => {
  const cas = source?.cas_numbers;
  const list = Array.isArray(cas)
    ? cas.filter((v) => v != null && String(v).trim() !== '')
    : cas
      ? [cas]
      : [];
  if (list.length === 0) return option.label;
  return `${option.label} (${list.join(', ')})`;
};

export const BASIC_FILTER_CONFIGS = [
  {
    key: 'names',
    labelKey: 'active-substance|filters.Active substance name',
    field: 'name.raw',
    // Client spec: typing in this dropdown must match against the substance
    // name, its CAS number(s), and its synonyms. Object format enables the
    // generic "matched via" subtitle in the dropdown when the hit comes from
    // a secondary field.
    searchFields: [
      { field: 'name.keyword', primary: true },
      {
        field: 'cas_numbers.keyword',
        label: 'CAS',
        sourceField: 'cas_numbers',
      },
      { field: 'synonyms.keyword', label: 'Synonyme', sourceField: 'synonyms' },
    ],
    // Pulls CAS numbers (and synonyms, for completeness) into each option so
    // the label can be composed as "name (cas1, cas2)".
    includeSourceFields: ['cas_numbers', 'synonyms'],
    formatOptionLabel: formatSubstanceNameLabel,
  },
  {
    key: 'cultures',
    labelKey: 'active-substance|filters.Cultures',
    field: 'cultures',
    value_field: 'id',
    label_field: 'french_name',
    languageConfig: LANGUAGE_FIELDS.cultures,
    filters: {
      filter_hide_in_pc_ma_search: false,
      filter_show_in_customer_filter_MA: true,
    },
  },
  {
    key: 'cultures_practices',
    labelKey: 'active-substance|filters.Cultural practices',
    field: 'cultures.cultural_practices',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'activity_sectors',
    labelKey: 'active-substance|filters.Activity sector',
    field: 'activity_sectors',
    value_field: 'id',
    label_field: 'name',
  },
];

export const AGRONOMIC_FILTER_CONFIGS = [
  {
    key: 'chemical_families',
    labelKey:
      'active-substance|filters.Chemical families or biological families',
    field: 'pesticide_group_associations.chemical_family',
    value_field: 'id',
    label_field: 'label',
  },
  {
    key: 'pesticide_groups',
    labelKey: 'active-substance|filters.Pesticide groups',
    field: 'pesticide_group_associations.pesticide_group',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'pesticide_categories',
    labelKey: 'active-substance|filters.Pesticide categories',
    field: 'pesticide_categories',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'pesticide_types',
    labelKey: 'active-substance|filters.Pesticide types',
    field: 'pesticide_types',
    value_field: 'id',
    label_field: 'label',
  },
  {
    key: 'activity_types',
    labelKey: 'active-substance|filters.Activity types',
    field: 'activity_type_filters',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'arla_culture_groups',
    labelKey: 'active-substance|filters.ARLA culture groups',
    field: 'cultures.arla_culture_group',
    value_field: 'id',
    label_field: 'group',
  },
  {
    key: 'resistance',
    labelKey: 'active-substance|filters.Resistance',
    field:
      'pesticide_group_associations.pesticide_group.risk_of_resistance_development',
    value_field: 'id',
    label_field: 'name',
  },
];

export const HEALTH_FILTER_CONFIGS = [
  {
    key: 'acute_toxicity',
    labelKey: 'active-substance|filters.Acute toxicity',
    field: 'symbols.health.acute_toxicity_symbol.calculated_value',
  },
  {
    key: 'long_term_effects',
    labelKey: 'active-substance|filters.Long term effects',
    field: 'symbols.health.long_term_effect_symbol.calculated_value',
  },
];

export const ENVIRONMENT_FILTER_CONFIGS = [
  {
    key: 'fish_or_daphnia',
    labelKey: 'active-substance|filters.Fish or daphnia',
    field: 'symbols.environment.fishes_symbol.calculated_value',
  },
  {
    key: 'birds',
    labelKey: 'active-substance|filters.Birds',
    field: 'symbols.environment.birds_symbol.calculated_value',
  },
  {
    key: 'bees',
    labelKey: 'active-substance|filters.Bees',
    field: 'symbols.environment.bees_symbol.calculated_value',
  },
  {
    key: 'persistence',
    labelKey: 'active-substance|filters.Persistence',
    field: 'symbols.environment.persistence_symbol.calculated_value',
  },
  {
    key: 'leaching_potential',
    labelKey: 'active-substance|filters.Leaching potential',
    field: 'symbols.environment.leaching_symbol.calculated_value',
  },
];

export const BASIC_FILTER_KEYS = BASIC_FILTER_CONFIGS.map((c) => c.key);
export const EXTRA_FILTER_KEYS = [
  ...AGRONOMIC_FILTER_CONFIGS.map((c) => c.key),
  ...HEALTH_FILTER_CONFIGS.map((c) => c.key),
  ...ENVIRONMENT_FILTER_CONFIGS.map((c) => c.key),
];

// Union of every configurable filter — used by FilterSection to compute the
// cross-field facet context passed to each ElasticsearchSelect.
export const ALL_FILTER_CONFIGS = [
  ...BASIC_FILTER_CONFIGS,
  ...AGRONOMIC_FILTER_CONFIGS,
  ...HEALTH_FILTER_CONFIGS,
  ...ENVIRONMENT_FILTER_CONFIGS,
];

export const DOCUMENT_TYPE = 'SubstanceActiveDocument';
